import { z } from 'zod';
import { localDateSchema, syncColumnsSchema } from './common.js';

/**
 * Compra y despensa (Fase 5, docs/DATA_MODEL.md §13,
 * docs/NUTRITION_ENGINE.md §12).
 *
 * Los alimentos se referencian por `slug` del catalogo en codigo, igual que en
 * nutricion. Lo que se persiste aqui es lo que depende del usuario: lo que
 * tiene en casa, lo que hay que comprar y lo que ya ha cogido del lineal.
 */

// --------------------------------------------------------------------------
// Vocabulario persistido
// --------------------------------------------------------------------------

/**
 * Seccion de supermercado. Vive aqui, y no en el catalogo del dominio, porque
 * `shopping_list_items.section` la persiste y forma parte del CHECK de la
 * tabla: los enums que llegan a la base de datos son contrato, no detalle del
 * motor. El catalogo de alimentos importa este tipo.
 *
 * Son las diez del §9.1 de PRODUCT_SPEC.md, y no dependen de ninguna cadena de
 * supermercados concreta.
 */
export const supermarketSectionSchema = z.enum([
  'produce',
  'meat',
  'fish',
  'dairy',
  'eggs',
  'cereals',
  'legumes',
  'frozen',
  'canned',
  'other',
]);
export type SupermarketSection = z.infer<typeof supermarketSectionSchema>;

/**
 * Orden de recorrido del supermercado. El enum no garantiza orden; esta lista
 * si, y es la que usa la pantalla para agrupar.
 */
export const SECTION_ORDER: readonly SupermarketSection[] = [
  'produce',
  'meat',
  'fish',
  'eggs',
  'dairy',
  'cereals',
  'legumes',
  'canned',
  'frozen',
  'other',
];

/** Unidad de una cantidad de compra o de despensa. */
export const measureUnitSchema = z.enum(['g', 'ml', 'unit']);
export type MeasureUnit = z.infer<typeof measureUnitSchema>;

export const shoppingListStatusSchema = z.enum(['active', 'archived']);
export type ShoppingListStatus = z.infer<typeof shoppingListStatusSchema>;

// --------------------------------------------------------------------------
// Despensa
// --------------------------------------------------------------------------

/**
 * Lo que hay en casa. Sin caducidades, lotes ni ubicaciones: no es un ERP
 * domestico (§9.3). **No se descuenta solo al cocinar**, porque seria una
 * fuente constante de datos incorrectos; lo ajusta el usuario cuando quiere.
 */
export const pantryItemSchema = syncColumnsSchema.extend({
  foodSlug: z.string().min(1).max(64),
  quantity: z.number().min(0).max(100000),
  unit: measureUnitSchema,
});

export type PantryItem = z.infer<typeof pantryItemSchema>;

// --------------------------------------------------------------------------
// Lista de compra
// --------------------------------------------------------------------------

export const shoppingListSchema = syncColumnsSchema.extend({
  rangeStart: localDateSchema,
  rangeEnd: localDateSchema,
  generatedAt: z.iso.datetime({ offset: true }),
  status: shoppingListStatusSchema,
  mealPlanId: z.uuid().nullable(),
  /**
   * Existencias en el momento de generar, por `foodSlug`. Se guarda para poder
   * explicar la resta meses despues: si manana se vacia la despensa, el «se
   * restaron 300 g» de esta lista tiene que seguir siendo cierto.
   */
  pantrySnapshot: z.record(z.string(), z.number()).default({}),
});

export type ShoppingList = z.infer<typeof shoppingListSchema>;

/**
 * Una linea de la lista.
 *
 * `needed*` y `purchase*` son campos SEPARADOS y no se mezclan nunca
 * (requisito §30 del prompt): «necesitas 870 g» y «compra 2 x 500 g» son dos
 * numeros distintos, y el segundo no sustituye al primero.
 */
export const shoppingListItemSchema = syncColumnsSchema.extend({
  shoppingListId: z.uuid(),
  foodSlug: z.string().min(1).max(64),
  /** Copia de `foods.section` al generar: la lista no se reordena sola. */
  section: supermarketSectionSchema,

  neededAmount: z.number().min(0).max(1000000),
  neededUnit: measureUnitSchema,
  pantryDeducted: z.number().min(0).max(1000000).default(0),

  purchaseAmount: z.number().min(0).max(1000000),
  purchaseUnit: measureUnitSchema,
  /** Formato comercial usado, si el alimento declara uno. */
  packSize: z.number().positive().max(100000).nullable(),
  packCount: z.number().int().min(0).max(500).nullable(),

  isChecked: z.boolean().default(false),
  /** Por que la linea es como es; sobre todo, que unidad no se pudo convertir. */
  reasonCodes: z.array(z.string().min(1)).default([]),
});

export type ShoppingListItem = z.infer<typeof shoppingListItemSchema>;
